// EN-11: shared, framework-free helpers for the Historic Data, View Passages
// and Report Manager filters. Everything here is UTC and pure so it can be
// unit tested without a browser.

import type { IPigRunForDevice } from '@/models/PigRunForDevice';

/** Lower bound accepted by SQL Server datetime, used when no From date is set. */
export const HISTORY_MIN_DATE = '1753-01-01T00:00:00.000Z';
/** Upper bound used when no To date is set. */
export const HISTORY_MAX_DATE = '9999-12-31T23:59:59.997Z';

export interface SelectOption<T> {
  label: string;
  value: T;
}

/** True when `value` is a real calendar date in `YYYY/MM/DD` form. */
export function isCompleteCalendarDate(value: string): boolean {
  const match = /^(\d{4})\/(\d{2})\/(\d{2})$/.exec(value || '');
  if (!match) return false;
  const [, year, month, day] = match.map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return (
    date.getUTCFullYear() === year &&
    date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day
  );
}

/**
 * A Historic Data date input is usable when it is empty (no bound) or a
 * complete calendar date. Anything else (partly typed, 2026/02/30) is invalid.
 */
export function isUsableFilterDate(value: string): boolean {
  return !value || isCompleteCalendarDate(value);
}

function calendarDateBoundary(value: string, endOfDay: boolean): string {
  const [year, month, day] = value.split('/').map(Number);
  return new Date(
    Date.UTC(
      year,
      month - 1,
      day,
      endOfDay ? 23 : 0,
      endOfDay ? 59 : 0,
      endOfDay ? 59 : 0,
      endOfDay ? 999 : 0
    )
  ).toISOString();
}

/**
 * Builds the request date range for the Historic Data tables. Either bound
 * may be left empty (EN-11: the filter no longer needs both dates); an empty
 * bound falls back to the widest range the API accepts. Returns null when an
 * input is invalid or To is before From.
 */
export function buildHistoryDateRange(
  fromDate: string,
  toDate: string
): { startDate: string; endDate: string } | null {
  if (!isUsableFilterDate(fromDate) || !isUsableFilterDate(toDate)) return null;
  if (fromDate && toDate && toDate < fromDate) return null;
  return {
    startDate: fromDate
      ? calendarDateBoundary(fromDate, false)
      : HISTORY_MIN_DATE,
    endDate: toDate ? calendarDateBoundary(toDate, true) : HISTORY_MAX_DATE,
  };
}

/**
 * Adds the optional pig run / marker filter fields to a history request.
 * Fields are only set when chosen, so older servers and the mobile app see
 * the same payload as before. The marker is only sent together with a run.
 */
export function withPigRunFilter<
  T extends { pigRunId?: number; pipelineSectionMarkerId?: number }
>(request: T, pigRunId: number | null, markerId?: number | null): T {
  if (pigRunId == null) return request;
  const next: T = { ...request, pigRunId };
  if (markerId != null) next.pipelineSectionMarkerId = markerId;
  return next;
}

/** Parses an API timestamp (UTC, with or without a trailing zone) to ms. */
export function parseApiUtcTimestamp(
  value: string | Date | null | undefined
): number | null {
  if (!value) return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isNaN(t) ? null : t;
  }
  const raw = String(value).trim();
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);
  const iso = hasZone ? raw : `${raw.replace(' ', 'T')}Z`;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * Parses a UTC date-time picker value: `YYYY-MM-DD HH:mm` or
 * `YYYY-MM-DD HH:mm:ss`. Returns undefined for an empty value (no bound) and
 * null for an invalid one. Unlike DateCommon this rejects rolled-over dates
 * such as 2026-02-30.
 */
export function parseUtcDateTimeInput(
  value: string | null | undefined
): Date | null | undefined {
  const raw = (value || '').trim();
  if (!raw) return undefined;
  const match =
    /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/.exec(raw);
  if (!match) return null;
  const [year, month, day, hour, minute] = match.slice(1, 6).map(Number);
  const second = match[6] !== undefined ? Number(match[6]) : 0;
  if (hour > 23 || minute > 59 || second > 59) return null;
  const date = new Date(Date.UTC(year, month - 1, day, hour, minute, second));
  if (
    date.getUTCFullYear() !== year ||
    date.getUTCMonth() !== month - 1 ||
    date.getUTCDate() !== day
  ) {
    return null;
  }
  return date;
}

export interface UtcRange {
  start?: Date;
  end?: Date;
  error?: string;
}

/**
 * Validates an optional From/To UTC date-time pair. A To value without
 * seconds covers that whole minute, so `10:05` includes 10:05:59.
 */
export function buildUtcRange(from: string, to: string): UtcRange {
  const start = parseUtcDateTimeInput(from);
  const endParsed = parseUtcDateTimeInput(to);
  if (start === null)
    return { error: 'The From time is not a valid UTC date/time.' };
  if (endParsed === null)
    return { error: 'The To time is not a valid UTC date/time.' };
  let end = endParsed;
  if (end) {
    // The To bound is inclusive: cover the whole minute (no seconds given)
    // or the whole second (seconds given).
    const hasSeconds = /:\d{2}:\d{2}$/.test((to || '').trim());
    end = new Date(end.getTime() + (hasSeconds ? 999 : 59999));
  }
  if (start && end && start.getTime() > end.getTime()) {
    return { error: 'The From time must be on or before the To time.' };
  }
  return { start, end };
}

/** Keeps rows whose timestamp falls inside the (inclusive) range. */
export function filterRowsByUtcRange<T>(
  rows: T[],
  getTimestamp: (row: T) => string | Date | null | undefined,
  range: UtcRange
): T[] {
  if (range.error) return rows;
  const startMs = range.start ? range.start.getTime() : null;
  const endMs = range.end ? range.end.getTime() : null;
  if (startMs === null && endMs === null) return rows;
  return rows.filter((row) => {
    const t = parseApiUtcTimestamp(getTimestamp(row));
    if (t === null) return false;
    if (startMs !== null && t < startMs) return false;
    if (endMs !== null && t > endMs) return false;
    return true;
  });
}

function formatUtcMinute(value: string | null | undefined): string {
  const t = parseApiUtcTimestamp(value);
  if (t === null) return '';
  return new Date(t).toISOString().slice(0, 16).replace('T', ' ');
}

const LIFECYCLE_LABELS: Record<string, string> = {
  C: 'Created',
  R: 'Running',
  D: 'Done',
  X: 'Cancelled',
};

/** Picker options for the Historic Data pig run filter. */
export function buildPigRunFilterOptions(
  runs: IPigRunForDevice[]
): SelectOption<number>[] {
  return (runs || []).map((run) => {
    const state = LIFECYCLE_LABELS[run.lifecycleState] || run.lifecycleState;
    const launch = formatUtcMinute(run.launchDate);
    const parts = [run.name];
    if (launch) parts.push(launch);
    if (state) parts.push(state);
    return { label: parts.join(' / '), value: run.pigRunId };
  });
}

export interface MarkerLike {
  pipelineSectionMarkerId?: number | null;
  name?: string | null;
  trackState?: string | null;
  sortOrder?: number | null;
}

/**
 * Picker options for a pig run's markers, in pipeline order. SKIP markers are
 * kept (EN-11 decision 7) and labelled so they are easy to tell apart.
 */
export function buildMarkerFilterOptions(
  markers: MarkerLike[]
): SelectOption<number>[] {
  return [...(markers || [])]
    .filter((m) => m.pipelineSectionMarkerId != null)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    .map((m) => ({
      label: (m.name || '') + (m.trackState === 'S' ? ' (SKIP)' : ''),
      value: m.pipelineSectionMarkerId as number,
    }));
}

export interface MarkerPassageLink {
  pipelineSectionMarkerId?: number | null;
  runId?: number | null;
  pigRunId?: number | null;
  [key: string]: any;
}

/**
 * A passage can be linked to markers of several pig runs. When a pig run (and
 * marker) filter is active, show the link that matches it instead of the
 * first one, so the Pig Run / Marker columns agree with the filter.
 */
export function pickMarkerPassage<T extends MarkerPassageLink>(
  links: T[] | null | undefined,
  pigRunId: number | null,
  markerId?: number | null
): T | null {
  if (!Array.isArray(links) || links.length === 0) return null;
  const runOf = (l: T) => l.runId ?? l.pigRunId ?? null;
  if (markerId != null) {
    const byMarker = links.find((l) => l.pipelineSectionMarkerId === markerId);
    if (byMarker) return byMarker;
  }
  if (pigRunId != null) {
    const byRun = links.find((l) => runOf(l) === pigRunId);
    if (byRun) return byRun;
  }
  return links[0];
}
